import styled from 'styled-components';
import Button from '../../Button';
import { FaChevronLeft } from 'react-icons/fa';
import { EditBtn, InfoBtn, StatusDot } from '../styles';
import { inverseModuleStatuses, MODULE_BACKGROUND_COLORS, primaryMatch } from '../enums';
import EditableCell from '../../EditableCell';
import { useMemo } from 'react';
import { capitalize, round } from 'lodash';
import Dropdown from '../../Dropdown';
import { DESIGN_MATURITY_OPTIONS, STANDARDIZATION_INTENT_SCORE_OPTIONS } from '../../../config/enums';
import React from 'react';

export const columns = [
  {
    Header: 'Edit',
    Footer: () => null,
    rowSpan: 3,
    getCustomStyles: () => ({
      opacity: 0,
      pointerEvents: 'none',
    }),
    columns: [
      {
        Header: 'EDIT MODULE INVISIBLE 1',
        getCustomStyles: () => ({
          display: 'none',
        }),
        Footer: () => null,
        columns: [
          {
            Header: 'EDIT MODULE INVISIBLE 2',
            getCustomStyles: () => ({
              display: 'none',
            }),
            Footer: () => null,
            columns: [
              {
                Header: 'EDIT MODULE INVISIBLE 3',
                accessor: 'id',
                getCustomStyles: () => ({
                  display: 'none',
                  width: 'fit-content',
                }),
                Footer: () => null,
                Cell: ({ row, onEditModule, isAdmin }) =>
                  isAdmin ? (
                    <EditBtn
                      onClick={() => onEditModule(row.original)}
                      wrapperStyle={{
                        width: '100%',
                      }}
                    />
                  ) : (
                    <InfoBtn
                      onClick={() => onEditModule(row.original)}
                      wrapperStyle={{
                        width: '100%',
                      }}
                    />
                  ),
              },
            ],
          },
        ],
      },
    ],
  },
  {
    Header: ({ projectName }) => projectName,
    Footer: () => null,
    id: 'projectName',
    columns: [
      {
        Header: 'Tag',
        rowSpan: 2,
        Footer: () => null,
        columns: [
          {
            Header: 'TAG INVISIBLE 1.0',
            getCustomStyles: () => ({
              display: 'none',
            }),
            Footer: () => null,
            columns: [
              {
                Header: 'TAG INVISIBLE 2.0',
                getCustomStyles: () => ({
                  display: 'none',
                  width: '8%',
                }),
                accessor: 'tsCode',
                Footer: () => null,
                Cell: ({ row, value }) => {
                  return (
                    <TagCell>
                      <StatusDot color={inverseModuleStatuses[row.original.status]?.color} />
                      {value}
                    </TagCell>
                  );
                },
              },
            ],
          },
        ],
      },
      {
        Header: 'Designation',
        rowSpan: 2,
        Footer: () => null,
        columns: [
          {
            Header: 'Designation INVISIBLE 1.0',
            getCustomStyles: () => ({
              display: 'none',
            }),
            Footer: () => null,
            columns: [
              {
                Header: 'Designation INVISIBLE 2.0',
                getCustomStyles: () => ({
                  display: 'none',
                  width: '12%',
                }),
                accessor: 'name',
                Footer: 'Total gross weight (mT):',
              },
            ],
          },
        ],
      },
      {
        Header: () => (
          <ColumnDisplay>
            <span>Gross weight (dry)</span>
            <span>(mT)</span>
          </ColumnDisplay>
        ),
        rowSpan: 2,
        id: 'weight-column',
        Footer: () => null,
        columns: [
          {
            Header: 'Weight INVISIBLE 1.0',
            getCustomStyles: () => ({
              display: 'none',
            }),
            Footer: () => null,
            columns: [
              {
                Header: 'Weight INVISIBLE 2.0',
                getCustomStyles: () => ({
                  display: 'none',
                  width: '7%',
                }),
                accessor: 'estimatedWeight',
                Footer: ({ data, integrationWeight }) => {
                  const modulesTotal = useMemo(() => {
                    const weights = data.map((d) => parseToFloatOrReturnZero(d.estimatedWeight));
                    return weights.reduce((total, curr) => total + curr, 0);
                  }, [data]);
                  const total = modulesTotal + parseToFloatOrReturnZero(integrationWeight);
                  return total;
                },
                Cell: (props) =>
                  props.isAdmin ? (
                    <EditableCell
                      {...props}
                      type='number'
                    />
                  ) : (
                    props.value
                  ),
              },
            ],
          },
        ],
      },
    ],
  },
  {
    Header: 'Standardization Indicator',
    rowSpan: 1,
    Footer: () => null,
    columns: [
      {
        Header: 'Vendor or EPC',
        rowSpan: 2,
        Footer: () => null,
        columns: [
          {
            Header: 'Vendor or EPC INVISIBLE',
            getCustomStyles: () => ({
              display: 'none',
            }),
            Footer: () => null,
            columns: [
              {
                Header: 'Vendor or EPC INVISIBLE 2.0',
                getCustomStyles: () => ({
                  display: 'none',
                  width: '6%',
                }),
                accessor: 'vendor',
                Footer: () => null,
                Cell: ({ value, row }) => {
                  let val = value ?? 'No';
                  if (row.original.bespoke) {
                    val = val + ' (B';
                    if (row.original.repeatable) {
                      val = val + 'R)';
                    } else {
                      val = val + ')';
                    }
                  }
                  return val;
                },
              },
            ],
          },
        ],
      },
      {
        Header: 'Reference Module',
        Footer: () => null,
        columns: [
          {
            Header: 'Module Tag',
            Footer: () => null,
            columns: [
              {
                Header: 'Module Tag INVISIBLE 2.0',
                getCustomStyles: () => ({
                  display: 'none',
                  width: '10%',
                }),
                Cell: ({ value, row, projectPhase, isAdmin, updateCellData }) =>
                {
                  let referenceModuleTsCode = row.original.referenceModuleTsCode ? row.original.referenceModuleTsCode || '' : row.original.referenceModuleName ? row.original.referenceModuleName.split(' ')[0] : '';
                  let referenceModuleProjectNr = row.original.referenceModuleProject ? row.original.referenceModuleProject.split(' ')[0] : '';
                  return <><div>{referenceModuleProjectNr}</div><div>{referenceModuleTsCode}</div></>;
                },
                accessor: 'referenceModuleName',
                Footer: () => null,
              },
            ],
          },
          {
            Header: 'Maturity',
            Footer: () => null,
            columns: [
              {
                Header: 'Maturity INVISIBLE 2.1',
                getCustomStyles: () => ({
                  display: 'none',
                  width: '11%',
                }),
                Cell: ({ value, row, projectPhase, isAdmin, updateCellData }) =>
                  !row.original.bespoke && isAdmin ? (
                    <DropdownCell>
                      <Dropdown
                        value={value}
                        options={DESIGN_MATURITY_OPTIONS}
                        placeholder='Design Maturity'
                        onChange={(option) => updateCellData(row?.index, 'expectedMaturityLevel', option.value)}
                      />
                    </DropdownCell>
                  ) : (
                    <DefaultCell>{value || '0%'}</DefaultCell>
                  ),
                Footer: () => null,
                accessor: 'expectedMaturityLevel',
              },
            ],
          },
        ],
      },
    ],
  },
  {
    Header: 'Standardization Score',
    rowSpan: 3,
    Footer: () => null,
    columns: [
      {
        Header: 'Standardization Score INVISIBLE 0',
        getCustomStyles: () => ({
          display: 'none',
        }),
        Footer: () => null,
        columns: [
          {
            Header: 'Standardization Score INVISIBLE 1',
            getCustomStyles: () => ({
              display: 'none',
            }),
            Footer: () => null,
            columns: [
              {
                Header: 'Standardization Score INVISIBLE 2.1',
                accessor: 'siScore',
                getCustomStyles: () => ({
                  display: 'none',
                  width: '15%',
                }),
                // value is the siScore, row.original is the whole data of the module i.e the module
                Cell: ({ value, row, projectPhase, isAdmin, updateCellData }) =>
                  projectPhase === 1 && !row.original.bespoke && isAdmin ? (
                    <DropdownCell>
                      <Dropdown
                        value={value}
                        options={STANDARDIZATION_INTENT_SCORE_OPTIONS}
                        placeholder='Intent Score'
                        onChange={(option) => updateCellData(row?.index, 'siScore', option.value)}
                      />
                    </DropdownCell>
                  ) : (
                    <DefaultCell bg={getSiScoreCellBackground(row.original)}>
                      {Boolean(value) && !isNaN(value) ? value : 0}%
                    </DefaultCell>
                  ),
                Footer: () => null,
              },
            ],
          },
        ],
      },
    ],
  },
  {
    Header: 'Major Findings',
    Footer: () => null,
    rowSpan: 3,
    columns: [
      {
        Header: 'Major Findings INVISIBLE 0',
        getCustomStyles: () => ({
          display: 'none',
        }),
        Footer: () => null,
        columns: [
          {
            Header: 'Major Findings INVISIBLE 1',
            getCustomStyles: () => ({
              display: 'none',
            }),
            Footer: () => null,
            columns: [
              {
                Header: 'Major Findings INVISIBLE 2.0',
                getCustomStyles: () => ({
                  display: 'none',
                  width: '25%',
                }),
                Footer: () => null,
                accessor: 'scoringTable.totalReasoning',
                Cell: (props) =>
                  !props.isAdmin || props.projectPhase !== 1 ? (
                    <TextCell>{props.value}</TextCell>
                  ) : (
                    <EditableCell
                      {...props}
                      type='text'
                    />
                  ),
              },
              {
                Header: 'Major Findings INVISIBLE 2.1',
                getCustomStyles: () => ({
                  display: 'none',
                  minWidth: '50px',
                  width: '5%',
                }),
                id: 'expander',
                Footer: () => null,
                Cell: ({ row }) =>
                  <ToggleSubcomponent
                    {...row.getToggleRowExpandedProps()}
                    isExpanded={row.isExpanded}
                  />
              },
            ],
          },
        ],
      },
    ],
  },
];

const TagCell = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const DefaultCell = styled(TagCell)`
  padding: 0.5rem;
  background-color: ${({ bg }) => bg || 'transparent'};
`;

export const ToggleSubcomponent = styled(Button).attrs(({ size = '1.2rem', isExpanded }) => ({
  type: 'transparentBackgroundButton',
  icon: FaChevronLeft,
  wrapperStyle: {
    height: '100%',
  },
  iconProps: {
    size: size,
    transform: `rotate(${isExpanded ? '-90' : 0})`,
  },
}))``;

const TextCell = styled.p`
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 3; /* number of lines to show */
  line-clamp: 3;
  -webkit-box-orient: vertical;
`;

const DropdownCell = styled.div`
  width: 90%;
  margin: auto;
`;

const ColumnDisplay = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

export const getSiScoreCellBackground = (row) => {
  const { bespoke = false, siScore = null, repeatable = false } = row;

  if (!bespoke && siScore === null) {
    return;
  }
  let color = '';
  if (bespoke) {
    if (repeatable) {
      color = MODULE_BACKGROUND_COLORS.LIGHT_PEACH;
    } else {
      color = MODULE_BACKGROUND_COLORS.PEACH;
    }
  } else if (siScore < 15) {
    color = MODULE_BACKGROUND_COLORS.ORANGE;
  } else if (siScore >= 15 && siScore < 65) {
    color = MODULE_BACKGROUND_COLORS.DARK_GREY;
  } else if (siScore >= 65 && siScore < 85) {
    color = MODULE_BACKGROUND_COLORS.YELLOW;
  } else {
    color = MODULE_BACKGROUND_COLORS.GREEN;
  }

  return color;
};

export const parseToFloatOrReturnZero = (value) => parseFloat(!value || isNaN(value) ? 0 : value);
